import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { API, api, formatApiErrorDetail, getToken } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function CvthequeDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const openContact = sp.get("contact") === "1";
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContact, setShowContact] = useState(openContact);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cvtheque/consultants/${id}`);
      setItem(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
      navigate("/cvtheque");
    } finally { setLoading(false); }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);

  const downloadCv = async () => {
    try {
      const res = await fetch(`${API}/cvtheque/consultants/${id}/cv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (_) { toast.error(t("common.error_generic")); }
  };

  const sendContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/cvtheque/consultants/${id}/contact`, { message });
      toast.success(t("cvtheque.contact_success"));
      setShowContact(false);
      navigate(`/messages/${data.thread_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setSending(false); }
  };

  if (loading || !item) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-10 text-center text-slate-500">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cvtheque-detail-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <Link to="/cvtheque" className="text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="cvtheque-detail-back">
          {t("cvtheque.detail_back")}
        </Link>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="cvtheque-detail-name">{item.display_name}</h1>
                {item.redact_identity && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 ring-1 ring-slate-200">
                    🔒 {t("cvtheque.identity_hidden")}
                  </span>
                )}
              </div>
              <p className="mt-1 text-base text-slate-700">{item.title}</p>
              <div className="mt-2 text-xs text-slate-500">
                {t(`roster.seniority_${item.seniority}`)} · {item.years_experience} {t("cvtheque.years_short")} · {t(`roster.remote_${item.remote_policy}`)}
                {item.location_city && ` · ${item.location_city}`}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-semibold text-slate-900">{item.tjm_target}/day</div>
              <div className="mt-1 text-xs text-slate-500">{t("roster.availability")} {fmtDate(item.availability_date)}</div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-1.5">
            {(item.skills || []).map((s) => (
              <span key={s} className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{s}</span>
            ))}
          </div>

          {item.bio && (
            <div className="mt-6">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("cvtheque.detail_about")}</h2>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">{item.bio}</p>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3 border-t border-slate-100 pt-6">
            <button
              type="button"
              onClick={downloadCv}
              disabled={!item.has_cv}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="cvtheque-detail-cv-btn"
            >
              {item.has_cv ? t("cvtheque.download_cv") : t("cvtheque.no_cv")}
            </button>
            <button
              type="button"
              onClick={() => setShowContact(true)}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
              data-testid="cvtheque-detail-contact-btn"
            >
              {t("cvtheque.contact_agency")}
            </button>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="cvtheque-detail-agency-card">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("cvtheque.detail_agency")}</h2>
          <div className="mt-2 flex items-center gap-3">
            {item.agency_has_logo ? (
              <img src={`${API}/orgs/${item.agency_org_id}/logo`} alt="" className="h-10 w-10 rounded-lg object-cover ring-1 ring-slate-200" />
            ) : (
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-200 text-sm font-bold text-slate-700">
                {(item.agency_name || "").slice(0, 1).toUpperCase()}
              </div>
            )}
            <Link to={`/agencies/${item.agency_org_id}`} className="text-base font-semibold text-indigo-700 hover:text-indigo-800">
              {item.agency_name}
            </Link>
          </div>
        </div>
      </main>

      {showContact && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4" role="dialog" aria-modal="true" data-testid="cvtheque-contact-modal">
          <form onSubmit={sendContact} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">
              {t("cvtheque.contact_modal_title", { agency: item.agency_name })}
            </h2>
            <p className="mt-2 text-sm text-slate-600">{t("cvtheque.contact_modal_hint")}</p>
            <label className="mt-4 block text-sm font-medium text-slate-700">{t("cvtheque.contact_message")}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              required
              minLength={20}
              placeholder={t("cvtheque.contact_message_placeholder")}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              data-testid="cvtheque-contact-message"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setShowContact(false)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="cvtheque-contact-cancel">
                {t("common.cancel")}
              </button>
              <button type="submit" disabled={sending || message.length < 20} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60" data-testid="cvtheque-contact-send">
                {sending ? t("common.loading") : t("cvtheque.contact_send")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
